<template>
  <div class="multiple_choice_list" :style="multipleChoiceListStyle">
    <div
      v-for="option in options"
      :key="option.displayValue"
      class="multiple_choice_list__option"
      tabindex="0"
      :class="{
        'multiple_choice_list__option--selected': inputValue.includes(
          option.value
        ),
        'multiple_choice_list__option--block': isBlock,
      }"
      @click="toggleValue(option.value)"
      @keydown.enter.prevent="toggleValue(option.value)"
      @keydown.space.prevent="toggleValue(option.value)"
    >
      <Checkbox :tabindex="-1" :value="inputValue.includes(option.value)" />
      {{ option.displayValue }}
    </div>
    <ValidationMessage
      :displayInvalid="displayInvalid"
      :validation-message="externalValidationErrorMessage || validationMessage"
    />
  </div>
</template>

<script>
import BaseComponent from "../BaseComponent";
import InputBase from "../atoms/Inputs/mixins/InputBase";
import Checkbox from "../atoms/Inputs/Checkbox.vue";

/**
 * @group Inputs
 * Displays a list of options out of which the user can select multiple options.
 */
export default {
  mixins: [BaseComponent, InputBase],
  name: "MultipleChoiceList",
  components: {
    Checkbox,
  },
  props: {
    value: {
      type: Array,
    },
    options: {
      type: Array,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    validationMessage: {
      type: String,
      default: "Please select an option",
    },
    // Display option. When true every option is displayed as a block.
    isBlock: {
      type: Boolean,
      default: false,
    },
    // Display option. Defines over how much columns the option are spread.
    columns: {
      type: Number,
      default: 1,
    },
    enableExternalValidation: {
      type: Boolean,
      default: false,
    },
    externalValidationError: {
      type: Boolean,
    },
    externalValidationErrorMessage: {
      type: String,
    },
  },
  data() {
    return {
      isTouched: false,
    };
  },
  computed: {
    isValid() {
      if (this.enableExternalValidation) return this.externalValidationError;

      let isValid = true;
      if (this.required && this.inputValue.length === 0) {
        isValid = false;
      }
      return isValid;
    },
    multipleChoiceListStyle() {
      let columnTemplate = "";
      for (let index = 0; index < this.columns; index += 1) {
        columnTemplate += "1fr ";
      }
      return {
        "grid-template-columns": columnTemplate,
      };
    },
    displayInvalid() {
      return (
        (this.isShowingValidationMessage && !this.isValid) ||
        (this.isTouched && !this.isValid)
      );
    },
  },
  watch: {
    inputValue() {
      this.isTouched = true;
    },
  },
  methods: {
    toggleValue(value) {
      if (this.inputValue.includes(value)) {
        this.inputValue = this.inputValue.filter((item) => item !== value);
      } else {
        this.inputValue.push(value);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../style_variables/style_variables.scss";

.multiple_choice_list {
  width: 100%;
  display: inline-grid;
  grid-column-gap: 8px;
  max-width: $input_max_width;
}
.multiple_choice_list__option {
  @extend %body2_style;
  display: inline-flex;
  align-items: center;
  max-width: $input_max_width;
  height: 40px;
  padding-right: 20px;
  overflow: hidden;
  &:hover {
    cursor: pointer;
  }
  &.multiple_choice_list__option--block {
    width: 100%;
    height: 56px;
    border: 1px solid $grey_french;
    background-color: $white;
    padding: 16px;
    margin-bottom: 8px;
    border-radius: 4px;
    &:hover {
      border-color: $grey_french;
    }
    &.multiple_choice_list__option--selected {
      border-color: $blue;
    }
  }
}
</style>
